import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Calendar, CheckCircle2, Clock, AlertCircle, Trash2, PlayCircle, Search, Filter, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SprintFormDialog } from "@/components/sprints/sprint-form-dialog";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { STAGE_NUMBER_MAP, type WorkflowStageValue } from "@/lib/constants/testing-constants";

interface Sprint {
  id: string;
  sprintNumber: string;
  sprintName: string | null;
  description: string | null;
  currentStage: WorkflowStageValue;
  stageStatus: "pending" | "in_progress" | "passed" | "blocked" | "completed" | "skipped" | "approved";
  projectId?: string | null;
  projectName?: string | null;
  module?: string | null;
  totalTables: number;
  completedTables: number;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  gate1Passed?: boolean;
  gate2Passed?: boolean;
  gate3Passed?: boolean;
  gate4Passed?: boolean;
  gate5Passed?: boolean;
  testPassRate?: string | null;
}

const stageStatusConfig = {
  pending: { label: "Pending", color: "bg-gray-500/10 text-gray-700 border-gray-500/20", icon: Clock },
  in_progress: { label: "In Progress", color: "bg-blue-500/10 text-blue-700 border-blue-500/20", icon: PlayCircle },
  passed: { label: "Passed", color: "bg-green-500/10 text-green-700 border-green-500/20", icon: CheckCircle2 },
  blocked: { label: "Blocked", color: "bg-red-500/10 text-red-700 border-red-500/20", icon: AlertCircle },
  completed: { label: "Completed", color: "bg-green-500/10 text-green-700 border-green-500/20", icon: CheckCircle2 },
  skipped: { label: "Skipped", color: "bg-gray-500/10 text-gray-700 border-gray-500/20", icon: Clock },
  approved: { label: "Approved", color: "bg-green-500/10 text-green-700 border-green-500/20", icon: CheckCircle2 },
};

export default function SprintList() {
  const [, navigate] = useLocation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sprintToDelete, setSprintToDelete] = useState<Sprint | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { toast} = useToast();
  const queryClient = useQueryClient();

  const { data: sprints, isLoading } = useQuery<Sprint[]>({
    queryKey: ["/api/project-manager/sprints"],
  });

  const handleCreateClick = () => {
    setEditingSprint(null);
    setDialogOpen(true);
  };

  const handleEditClick = (sprint: Sprint) => {
    setEditingSprint(sprint);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSprint(null);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/project-manager/sprints"] });
    handleDialogClose();
    toast({
      title: editingSprint ? "Sprint updated" : "Sprint created",
      description: editingSprint
        ? "Sprint has been updated successfully."
        : "New sprint has been created successfully.",
    });
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/project-manager/sprints/${id}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/project-manager/sprints"] });
      setDeleteDialogOpen(false);
      setSprintToDelete(null);
      toast({
        title: "Sprint deleted",
        description: "Sprint has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const handleDeleteClick = (sprint: Sprint, e: React.MouseEvent) => {
    e.stopPropagation();
    setSprintToDelete(sprint);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (sprintToDelete) {
      deleteMutation.mutate(sprintToDelete.id);
    }
  };

  const sortedSprints = sprints?.sort((a, b) => {
    // Sort by sprint number descending (newest first)
    return b.sprintNumber.localeCompare(a.sprintNumber);
  });

  // Filter sprints by search term and status
  const filteredSprints = sortedSprints?.filter((sprint) => {
    // Search filter
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      const matchesSearch =
        sprint.sprintNumber.toLowerCase().includes(lowerSearch) ||
        sprint.sprintName?.toLowerCase().includes(lowerSearch);
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter === "active" && sprint.currentStage === "complete") {
      return false;
    }
    if (statusFilter === "completed" && sprint.currentStage !== "complete") {
      return false;
    }

    return true;
  });

  const clearFilters = () => {
    setStatusFilter("");
  };

  const activeFiltersCount = statusFilter ? 1 : 0;

  return (
    <div className="h-full overflow-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Sprints</h1>
          <p className="text-muted-foreground mt-1">
            Manage development sprints and track implementation progress
          </p>
        </div>
        <Button onClick={handleCreateClick} data-testid="create-sprint-button">
          <Plus className="h-4 w-4 mr-2" />
          Add Sprint
        </Button>
      </div>

      {/* Search/Filter */}
      <Card className="p-3">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search sprints by number or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-9"
              data-testid="sprint-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
                data-testid="button-clear-search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filters Popover */}
          <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="button-filter-sprints">
                <Filter className="mr-2 h-4 w-4" />
                Filters
                {activeFiltersCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm">Filter Sprints</h4>
                  {activeFiltersCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear All
                    </Button>
                  )}
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={statusFilter || "none"}
                    onValueChange={(value) => setStatusFilter(value === "none" ? "" : value)}
                  >
                    <SelectTrigger data-testid="select-filter-status">
                      <SelectValue placeholder="All sprints" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">All Sprints</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </Card>

      {/* Sprint List */}
      <div className="grid gap-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Loading sprints...</div>
          </div>
        ) : filteredSprints && filteredSprints.length > 0 ? (
          filteredSprints.map((sprint) => {
            // Determine badge based on overall sprint completion (not current stage status)
            const isSprintComplete = sprint.currentStage === "complete";
            const statusInfo = isSprintComplete
              ? stageStatusConfig.completed
              : stageStatusConfig.in_progress;
            const StatusIcon = statusInfo.icon;
            const progress = sprint.totalTables > 0
              ? Math.round((sprint.completedTables / sprint.totalTables) * 100)
              : 0;
            const currentStageNumber = STAGE_NUMBER_MAP[sprint.currentStage] || 1;
            const stageProgress = Math.round((currentStageNumber / 14) * 100);
            const gatesPassed = [sprint.gate1Passed, sprint.gate2Passed, sprint.gate3Passed, sprint.gate4Passed, sprint.gate5Passed].filter(Boolean).length;

            return (
              <Card
                key={sprint.id}
                className="hover:border-primary/50 transition-colors cursor-pointer"
                data-testid={`sprint-card-${sprint.sprintNumber}`}
                onClick={() => navigate(`/project-manager/sprints/${sprint.id}`)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xl">
                          {sprint.sprintNumber}
                        </CardTitle>
                        <Badge className={statusInfo.color} variant="outline">
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </div>
                      {sprint.projectName && (
                        <CardDescription className="text-xs text-muted-foreground">
                          Project: {sprint.projectName}
                        </CardDescription>
                      )}
                      {sprint.sprintName && (
                        <CardDescription className="text-base font-medium text-foreground">
                          {sprint.sprintName}
                        </CardDescription>
                      )}
                      {sprint.description && (
                        <CardDescription className="line-clamp-2">
                          {sprint.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(sprint);
                        }}
                        data-testid={`edit-sprint-${sprint.sprintNumber}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => handleDeleteClick(sprint, e)}
                        data-testid={`delete-sprint-${sprint.sprintNumber}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Overall Progress</span>
                        <span className="font-medium">{stageProgress}%</span>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all"
                          style={{ width: `${stageProgress}%` }}
                        />
                      </div>
                    </div>

                    {/* Key Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      {/* Stage */}
                      <div className="space-y-1">
                        <div className="text-muted-foreground text-xs">Current Stage</div>
                        <div className="font-semibold">
                          Stage {currentStageNumber}/14
                        </div>
                        <div className="text-xs text-muted-foreground capitalize">{sprint.currentStage.replace(/_/g, ' ')}</div>
                      </div>

                      {/* Gates Passed */}
                      <div className="space-y-1">
                        <div className="text-muted-foreground text-xs">Gates Passed</div>
                        <div className="font-semibold">
                          {gatesPassed}/5
                        </div>
                        {sprint.testPassRate && (
                          <div className="text-xs text-muted-foreground">
                            {parseFloat(sprint.testPassRate).toFixed(0)}% pass rate
                          </div>
                        )}
                      </div>

                      {/* Tables Progress */}
                      <div className="space-y-1">
                        <div className="text-muted-foreground text-xs">Tables</div>
                        <div className="font-semibold">
                          {sprint.completedTables} / {sprint.totalTables}
                        </div>
                        {sprint.totalTables > 0 && (
                          <div className="text-xs text-muted-foreground">{progress}% complete</div>
                        )}
                      </div>

                      {/* Timeline */}
                      <div className="space-y-1">
                        <div className="text-muted-foreground text-xs">Timeline</div>
                        <div className="flex items-center gap-1 text-xs">
                          <Calendar className="h-3 w-3" />
                          {sprint.startDate && sprint.endDate ? (
                            <span>
                              {format(new Date(sprint.startDate), "MMM d")} - {format(new Date(sprint.endDate), "MMM d")}
                            </span>
                          ) : sprint.startDate ? (
                            <span>Started {format(new Date(sprint.startDate), "MMM d")}</span>
                          ) : (
                            <span className="text-muted-foreground">Not scheduled</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : sortedSprints && sortedSprints.length > 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-muted-foreground">
                No sprints match your search. Try a different search term.
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-muted-foreground">
                No sprints yet. Create your first sprint to get started.
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sprint Form Dialog */}
      <SprintFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleSuccess}
        sprint={editingSprint ? {
          id: editingSprint.id,
          sprint_number: editingSprint.sprintNumber,
          sprint_name: editingSprint.sprintName,
          description: editingSprint.description,
          current_stage: editingSprint.currentStage,
          stage_status: editingSprint.stageStatus,
          project_id: editingSprint.projectId,
          module: editingSprint.module,
          start_date: editingSprint.startDate,
          end_date: editingSprint.endDate,
        } : null}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sprint?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete sprint "{sprintToDelete?.sprintNumber}"?
              This action cannot be undone and will also delete all associated test checklists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
